import type { RecurringRevenueReport } from '@vy/types';
import { useMemo } from 'react';
import type { FilterField } from '../../../../components/new-design/vyTableFilter';
import { useLabels } from '../../../../hooks/requests/use-labels';
import {
  buildContractStartDateFilterField,
  buildContractStatusFilterField,
  buildCountryFilterField,
  buildCurrencyFilterField,
  buildCustomerFilterField,
  buildLabelsFilterField,
  buildLabelsOptions,
  buildPlanFilterField,
  buildProductFilterField,
  buildStateFilterField,
  collectIdNameOptions,
  collectRowProducts,
  collectUniqueOptions,
  filterNonEmptySelectFields,
  MULTISELECT_OPERATORS,
} from '../../reports-components/shared-filter-fields';
import {
  RECURRING_REVENUE_CURRENCY_FIELD_ID,
  RECURRING_REVENUE_FILTER_FIELD_IDS,
} from './recurring-revenue-filter-field-ids';

export { RECURRING_REVENUE_FILTER_FIELD_IDS };

export function useRecurringRevenueFilterFields(report: RecurringRevenueReport): FilterField[] {
  const { data: labelsData } = useLabels();

  return useMemo(() => {
    const customers = collectIdNameOptions(
      report.map((row) => ({ id: (row as { customerId?: string }).customerId, name: row.customerName })),
    );
    const subsidiaries = collectUniqueOptions(report.map((row) => row.subsidiaryName ?? null));
    const countries = collectUniqueOptions(report.map((row) => row.country ?? null));
    const states = collectUniqueOptions(report.map((row) => row.state ?? null));
    const plans = collectUniqueOptions(report.map((row) => row.contractName ?? null));

    // Product options come from the products present in the report's monthly
    // breakdowns (keyed by productId), matching how the table resolves product
    // names — the report payload doesn't carry catalogProductIds.
    const products = collectIdNameOptions(
      report.flatMap((row) => collectRowProducts(row).map((p) => ({ id: p.productId, name: p.productName }))),
    );

    const presentLabelIds = new Set(
      report.flatMap((row) => (row.labels ?? []).map((label) => label.id)),
    );
    const labelOptions = buildLabelsOptions(labelsData?.labels, presentLabelIds);

    const allFields: FilterField[] = [
      buildCustomerFilterField(customers),
      {
        id: RECURRING_REVENUE_FILTER_FIELD_IDS.Subsidiary,
        label: 'Subsidiary',
        inputType: 'select',
        operators: MULTISELECT_OPERATORS,
        options: subsidiaries,
      },
      buildCountryFilterField(countries),
      buildStateFilterField(states),
      buildProductFilterField(products),
      buildPlanFilterField(plans),
      buildContractStatusFilterField(),
      buildContractStartDateFilterField(),
      buildCurrencyFilterField(RECURRING_REVENUE_CURRENCY_FIELD_ID),
      buildLabelsFilterField(labelOptions),
    ];

    return filterNonEmptySelectFields(allFields);
  }, [report, labelsData]);
}
